<template>
  <div class="authentication-wrapper">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { AUTH_EVENTS, authBus } from "@/core/eventBusses/auth";
import storageService from "@/core/services/storage.service";
import { clearCache } from "@/core/utils/helpers";
import { STORAGE_KEYS } from "@/core/constants/storageKeys";
import { PATH } from "@/core/constants/path";
import { LoginResponse, Organization, User } from "../types";
import { USER_ROLES } from "@/core/enums/USER_ROLES";
import UTILS from "@/core/utils";
import { PERMISSIONS } from "@/core/enums/PERMISSIONS";
import { AUTH_MUTATIONS } from "../vuex/mutations";
import analyticServices from "@/analytic.service";
import { AUTH_ANALYTICS_EVENTS } from "../constants/analyticsEvents";

defineOptions({
  name: "AuthenticationWrapper",
});

const router = useRouter();
const store = useStore();

const clearStorage = () => {
  store.commit(AUTH_MUTATIONS.RESET_LOGIN_RESPONSE);
  store.commit("saveSearchFinancialProfiles", null);

  storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  storageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

  storageService.removeItem(STORAGE_KEYS.USER);
  storageService.removeItem(STORAGE_KEYS.PERMISSIONS);

  storageService.removeItem(STORAGE_KEYS.FINANCIAL_PROFILES);
  storageService.removeItem(STORAGE_KEYS.SECTORS);

  storageService.removeItem(STORAGE_KEYS.LAST_FETCHED_FEATURED_NEWS_TIME);
};

const logout = () => {
  clearCache();

  clearStorage();

  router.replace(PATH.ROOT);

  if (typeof window.FreshworksWidget === "function") {
    if (window.isFreshworksWidgetMounted) {
      window.FreshworksWidget("destroy");
    }
  }
};

const login = (response: LoginResponse) => {
  storageService.setItem(
    STORAGE_KEYS.ACCESS_TOKEN,
    response.token.access,
    false,
  );
  storageService.setItem(
    STORAGE_KEYS.REFRESH_TOKEN,
    response.token.refresh,
    false,
  );
  storageService.setItem(
    STORAGE_KEYS.PERMISSIONS,
    JSON.stringify(response.permissions),
  );

  const user = response.user;
  const isAgentUser = user.role_unique_id === USER_ROLES.AGENT;

  analyticServices.setUser(user);
  analyticServices.logActionEvent(AUTH_ANALYTICS_EVENTS.LOGIN, {
    email: user.email,
    role: user.role_name,
  });

  if (isAgentUser) {
    store.dispatch("profileModule/fetchAuthProfile");
  }

  UTILS.updateUser(response.user);

  router.push(PATH.DASHBOARD);
};

const syncStoredAuthState = () => {
  const accessToken = storageService.getItem<string>(
    STORAGE_KEYS.ACCESS_TOKEN,
    false,
  );
  const refreshToken = storageService.getItem<string>(
    STORAGE_KEYS.REFRESH_TOKEN,
    false,
  );
  const permissions = storageService.getItem<PERMISSIONS[]>(
    STORAGE_KEYS.PERMISSIONS,
  );
  const user = storageService.getItem<User>(STORAGE_KEYS.USER);
  const organization = storageService.getItem<Organization>(
    STORAGE_KEYS.ORGANIZATION,
  );

  if (accessToken || refreshToken) {
    if (!(user && permissions)) {
      logout();
    }
  }

  if (user) {
    UTILS.updateUser(user);
  }

  if (permissions) {
    UTILS.updatePermissions(permissions);
  }

  if (organization) {
    UTILS.updateOrganization(organization);
  }
};

onBeforeMount(() => {
  syncStoredAuthState();

  authBus.on(AUTH_EVENTS.LOGIN, (data) => {
    login(data as LoginResponse);
  });
  authBus.on(AUTH_EVENTS.LOGOUT, () => {
    logout();
  });
  authBus.on(AUTH_EVENTS.ROUTE_ON_ERROR_403, async (path) => {
    await router.push({
      path: PATH.Error_403,
      query: { routeId: path as string },
    });
    window.history.pushState({}, document.title, `${location.origin}${path}`);
  });
});
</script>
